<template>
    <div class="page-container">
        <div class="page-header">
            <h2 class="page-title">AI 写小说 · Studio</h2>
            <p class="page-subtitle">选择条件 → 生成灵感 → 生成大纲 → 一键成文！</p>
        </div>

        <div class="studio-layout">
            <!-- 左侧：条件选择 -->
            <div class="left-pane">
                <el-card shadow="never" class="card">
                    <div class="card-header">
                        <div class="icon-wrapper"><el-icon>
                                <Edit />
                            </el-icon></div>
                        <div>
                            <h3 class="auth-title">创作条件</h3>
                            <p class="tip-text">提示：<b>条件越少，生成效果越好</b>；也可直接使用随机条件</p>
                        </div>
                    </div>

                    <el-form :model="form" label-width="100px" class="elegant-form">
                        <el-form-item label="小说标题">
                            <el-input v-model="form.title" placeholder="留空则生成时自动命名" />
                        </el-form-item>

                        <el-form-item label="封面">
                            <el-upload class="avatar-uploader" :show-file-list="false"
                                :before-upload="beforeCoverUpload" :http-request="handleCoverUpload"
                                :disabled="uploadingCover">
                                <img v-if="form.cover_url" :src="form.cover_url" class="cover" />
                                <div v-else class="empty-cover">
                                    <el-icon class="plus-icon">
                                        <Plus v-if="!uploadingCover" />
                                        <Loading v-else class="loading-icon" />
                                    </el-icon>
                                </div>
                            </el-upload>
                        </el-form-item>

                        <!-- 随机条件开关 + 随机一组按钮 + 预览 -->
                        <el-form-item label="随机条件">
                            <div class="rand-row">
                                <el-switch v-model="form.random_mode" :active-value="1" :inactive-value="0"
                                    @change="onRandomToggle" />
                                <el-button size="small" :disabled="!form.random_mode" @click="randomize">
                                    <el-icon>
                                        <MagicStick />
                                    </el-icon> 随机一组
                                </el-button>
                                <el-checkbox v-model="randomFull" :disabled="!form.random_mode"
                                    title="勾选后会尽量为更多字段随机赋值">全量随机</el-checkbox>
                            </div>
                        </el-form-item>

                        <!-- 随机结果预览 -->
                        <el-form-item v-if="form.random_mode" label="随机结果">
                            <div class="tags-row">
                                <el-tag>{{ form.type || '（未设）' }}</el-tag>
                                <el-tag>{{ form.language || '（未设）' }}</el-tag>
                                <el-tag>{{ form.platform || '（未设）' }}</el-tag>
                                <el-tag>{{ form.length_type || '（未设）' }}</el-tag>
                                <el-tag>{{ form.pov || '（未设）' }}</el-tag>
                                <el-tag>{{ form.style_mode || '（未设）' }}</el-tag>
                                <el-tag>{{ form.era || '（未设）' }}</el-tag>
                                <el-tag>{{ form.genre || '（未设）' }}</el-tag>
                                <el-tag>{{ form.style || '（未设）' }}</el-tag>
                                <el-tag>金手指：{{ form.golden_finger === 1 ? (form.golden_finger_type || '是') : '否'
                                    }}</el-tag>

                                <template v-if="randomFull">
                                    <el-tag>{{ form.title_pattern || '（未设）' }}</el-tag>
                                    <el-tag>{{ form.female_role || '（未设）' }}</el-tag>
                                    <el-tag>{{ form.male_role || '（未设）' }}</el-tag>
                                    <el-tag>{{ form.villain_role || '（未设）' }}</el-tag>
                                    <el-tag>{{ form.male_job || '（未设）' }}</el-tag>
                                    <el-tag>{{ form.female_job || '（未设）' }}</el-tag>
                                    <el-tag>{{ form.plot || '（未设）' }}</el-tag>
                                    <el-tag>{{ form.world || '（未设）' }}</el-tag>
                                </template>
                            </div>
                        </el-form-item>

                        <!-- 手动选择（关闭随机时展示） -->
                        <el-row :gutter="12" v-show="!form.random_mode">
                            <el-col :span="12"><el-form-item label="类型"><el-select v-model="form.type" filterable
                                        allow-create><el-option v-for="v in types" :key="v" :label="v"
                                            :value="v" /></el-select></el-form-item></el-col>
                            <el-col :span="12"><el-form-item label="语言"><el-select v-model="form.language" filterable
                                        allow-create><el-option v-for="v in languages" :key="v" :label="v"
                                            :value="v" /></el-select></el-form-item></el-col>
                            <el-col :span="12"><el-form-item label="平台"><el-select v-model="form.platform" filterable
                                        allow-create><el-option v-for="p in platforms" :key="p" :label="p"
                                            :value="p" /></el-select></el-form-item></el-col>
                            <el-col :span="12"><el-form-item label="篇幅"><el-select v-model="form.length_type" filterable
                                        allow-create><el-option v-for="v in lengthTypes" :key="v" :label="v"
                                            :value="v" /></el-select></el-form-item></el-col>
                            <el-col :span="12"><el-form-item label="视角"><el-select v-model="form.pov" filterable
                                        allow-create><el-option v-for="v in povs" :key="v" :label="v"
                                            :value="v" /></el-select></el-form-item></el-col>
                            <el-col :span="12"><el-form-item label="文风"><el-select v-model="form.style_mode" filterable
                                        allow-create><el-option v-for="v in styleModes" :key="v" :label="v"
                                            :value="v" /></el-select></el-form-item></el-col>
                            <el-col :span="12"><el-form-item label="年代"><el-select v-model="form.era" filterable
                                        allow-create><el-option v-for="v in eras" :key="v" :label="v"
                                            :value="v" /></el-select></el-form-item></el-col>
                            <el-col :span="12"><el-form-item label="题材"><el-select v-model="form.genre" filterable
                                        allow-create><el-option v-for="g in genres" :key="g" :label="g"
                                            :value="g" /></el-select></el-form-item></el-col>
                            <el-col :span="12"><el-form-item label="金手指"><el-select v-model="form.golden_finger"
                                        filterable allow-create><el-option v-for="opt in goldenFingerOptions"
                                            :key="opt.value" :label="opt.label"
                                            :value="opt.value" /></el-select></el-form-item></el-col>
                            <el-col :span="12" v-if="form.golden_finger === 1"><el-form-item label="类型"><el-select
                                        v-model="form.golden_finger_type" filterable allow-create><el-option
                                            v-for="t in fingerTypes" :key="t" :label="t"
                                            :value="t" /></el-select></el-form-item></el-col>
                            <el-col :span="12"><el-form-item label="标题结构"><el-select v-model="form.title_pattern"
                                        filterable allow-create><el-option v-for="t in titlePatterns" :key="t"
                                            :label="t" :value="t" /></el-select></el-form-item></el-col>
                            <el-col :span="12"><el-form-item label="风格"><el-select v-model="form.style" filterable
                                        allow-create><el-option v-for="s in styles" :key="s" :label="s"
                                            :value="s" /></el-select></el-form-item></el-col>
                            <el-col :span="12"><el-form-item label="女主人设"><el-select v-model="form.female_role"
                                        filterable allow-create><el-option v-for="i in femaleRoles" :key="i" :label="i"
                                            :value="i" /></el-select></el-form-item></el-col>
                            <el-col :span="12"><el-form-item label="男主人设"><el-select v-model="form.male_role" filterable
                                        allow-create><el-option v-for="i in maleRoles" :key="i" :label="i"
                                            :value="i" /></el-select></el-form-item></el-col>
                            <el-col :span="12"><el-form-item label="反派人设"><el-select v-model="form.villain_role"
                                        filterable allow-create><el-option v-for="i in villainRoles" :key="i" :label="i"
                                            :value="i" /></el-select></el-form-item></el-col>
                            <el-col :span="12"><el-form-item label="男主职业"><el-select v-model="form.male_job" filterable
                                        allow-create><el-option v-for="i in maleJobs" :key="i" :label="i"
                                            :value="i" /></el-select></el-form-item></el-col>
                            <el-col :span="12"><el-form-item label="女主职业"><el-select v-model="form.female_job"
                                        filterable allow-create><el-option v-for="i in femaleJobs" :key="i" :label="i"
                                            :value="i" /></el-select></el-form-item></el-col>
                            <el-col :span="12"><el-form-item label="情节"><el-select v-model="form.plot" filterable
                                        allow-create><el-option v-for="i in plots" :key="i" :label="i"
                                            :value="i" /></el-select></el-form-item></el-col>
                            <el-col :span="12"><el-form-item label="时空背景"><el-select v-model="form.world" filterable
                                        allow-create><el-option v-for="i in worlds" :key="i" :label="i"
                                            :value="i" /></el-select></el-form-item></el-col>
                            <el-col :span="24"><el-form-item label="背景补充"><el-input v-model="form.background"
                                        placeholder="其它背景（可空）" /></el-form-item></el-col>
                        </el-row>

                        <el-form-item label="章节大纲">
                            <el-radio-group v-model="form.chapter_plan">
                                <el-radio :label="10">约10章（试跑）</el-radio>
                                <el-radio :label="100">约100章</el-radio>
                                <el-radio :label="400">约400章</el-radio>
                                <el-radio :label="550">约550章</el-radio>
                            </el-radio-group>
                            <el-input-number v-model="form.chapter_plan" :min="1" :max="2000" placeholder="输入章节数量"
                                controls-position="right" class="uniform-width" />
                        </el-form-item>

                        <el-alert type="warning" :closable="false" show-icon title="提示：务必保证字数充足，否则会写不完整！"
                            class="mb-12" />

                        <div class="btn-row">
                            <el-button type="primary" @click="createNovel" :loading="savingNovel">保存/创建小说</el-button>
                            <el-button @click="initChapters" :disabled="!novelId">初始化章节架构</el-button>
                        </div>

                        <!-- 状态提示 -->
                        <div class="mt-8">
                            <el-tag :type="chaptersInited ? 'success' : 'info'" class="mr-8">章节架构：{{ chaptersInited ?
                                '已初始化' : '未初始化'
                                }}</el-tag>
                            <el-tag :type="inspirationDone ? 'success' : 'info'" class="mr-8">灵感：{{ inspirationDone ?
                                '已生成并保存' : '未完成'
                                }}</el-tag>
                            <el-tag :type="outlineDone ? 'success' : 'info'">大纲：{{ outlineDone ? '已生成并保存' : '未完成'
                                }}</el-tag>
                        </div>
                    </el-form>
                </el-card>

                <el-card shadow="never" class="card">
                    <div class="card-header">
                        <div class="icon-wrapper"><el-icon>
                                <Document />
                            </el-icon></div>
                        <div>
                            <h3 class="auth-title">生成步骤</h3>
                            <p class="tip-text">按顺序进行（也可直接章节成文/续写）</p>
                        </div>
                    </div>

                    <div class="btn-col">
                        <el-button :disabled="!canGenInspiration" @click="stream('inspiration')"
                            class="uniform-width">生成灵感</el-button>
                        <el-button :disabled="!canGenOutline" @click="stream('outline')"
                            class="uniform-width">生成大纲</el-button>

                        <el-divider />
                        <el-form :inline="true">
                            <el-form-item label="章节序号">
                                <el-input-number v-model="chapterIndex" :min="1" />
                            </el-form-item>
                            <el-form-item>
                                <div class="button-container">
                                    <el-button type="success" :disabled="!canGenChapter"
                                        @click="stream('chapter')">生成章节</el-button>
                                    <el-button type="warning" :disabled="!canGenChapter" @click="stream('continue')"
                                        class="button-jg">续写本章</el-button>
                                    <el-button type="danger" :disabled="!es" @click="closeES"
                                        class="button-jg">停止</el-button>
                                </div>
                            </el-form-item>
                        </el-form>
                    </div>
                </el-card>
            </div>

            <!-- 右侧：实时生成 -->
            <div class="right-pane">
                <el-card shadow="never" class="card">
                    <div class="card-header">
                        <div class="icon-wrapper"><el-icon>
                                <View />
                            </el-icon></div>
                        <div>
                            <h3 class="auth-title">实时生成</h3>
                            <p class="tip-text">可用于直播演示，实时响应，效果好！</p>
                        </div>
                    </div>

                    <div class="stream-box" ref="streamBox">
                        <pre class="stream-text">{{ liveText }}</pre>
                    </div>

                    <div class="log-row">
                        <el-tag v-for="(l, i) in logs" :key="i" :type="l.type" class="mr-8">{{ l.msg }}</el-tag>
                    </div>
                </el-card>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, nextTick, onMounted, computed } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import config from '@/config'
import { Edit, Plus, Loading, Document, View, MagicStick } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { getUploadSignature, uploadToOSS, beforeUpload } from '@/utils/uploadUtils'
import { useNovelMeta } from '@/composables/useNovelMeta'
import { useRoute } from 'vue-router'

const route = useRoute()
const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
const storeId = userInfo.id

// ==== 枚举常量 ====
const {
    types, languages, platforms, lengthTypes, povs, styleModes, eras, genres,
    goldenFingerOptions, fingerTypes, titlePatterns, styles, femaleRoles, maleRoles,
    villainRoles, maleJobs, femaleJobs, plots, worlds, loadMeta
} = useNovelMeta()

const form = reactive({
    title: '', cover_url: '', random_mode: 1,
    type: '女频', language: '中文', platform: '番茄',
    length_type: '中长篇', pov: '第三人称', style_mode: '强爽点',
    era: '古代', genre: '言情', golden_finger: 0, golden_finger_type: '',
    title_pattern: '【情绪事件】+【反差】', style: '虐恋文',
    female_role: '', male_role: '', villain_role: '',
    male_job: '', female_job: '', plot: '', world: '',
    background: '无', chapter_plan: 100,
})

// —— 流程状态 —— //
const chaptersInited = ref(false)
const inspirationDone = ref(false)
const outlineDone = ref(false)

// —— 其余 UI 状态 —— //
const randomFull = ref(false)
const uploadingCover = ref(false)
const novelId = ref(null)
const chapterIndex = ref(1)

const liveText = ref('')
const rawText = ref('')
const logs = ref([])

let es = null
const savingNovel = ref(false)
const streamBox = ref(null)

// 打字效果
const queue = ref([])
let typer = null
const TYPING_MS = 10
function startTyper() {
    if (typer) return
    typer = setInterval(() => {
        if (queue.value.length === 0) { clearInterval(typer); typer = null; return }
        const head = queue.value[0]
        if (!head.text || head.text.length <= 0) { queue.value.shift(); return }
        const ch = head.text[0]
        head.text = head.text.slice(1)
        liveText.value += ch
        scrollToBottom()
    }, TYPING_MS)
}
function pushToType(text) {
    if (!text) return
    rawText.value += text
    queue.value.push({ text })
    startTyper()
}
function log(type, msg) { logs.value.push({ type, msg }) }
function scrollToBottom() {
    nextTick(() => {
        const box = streamBox.value
        if (box) box.scrollTop = box.scrollHeight
    })
}

// 随机化
function pick(arr) { return arr[Math.floor(Math.random() * arr.length)] }
function maybe(arr, fieldName) {
    // 如果该字段已经有自定义值（不在预设选项中），保留原值
    if (form[fieldName] && !arr.includes(form[fieldName])) {
        return form[fieldName];
    }
    return Math.random() < 0.7 ? pick(arr) : '';
}

function randomize() {
    // 只有在随机模式下才覆盖值
    if (form.random_mode !== 1) return;

    // 基础信息随机
    form.type = pick(types.value);
    form.language = pick(languages.value);
    form.platform = pick(platforms.value);
    form.length_type = pick(lengthTypes.value);
    form.pov = pick(povs.value);
    form.style_mode = pick(styleModes.value);
    form.era = pick(eras.value);

    // 题材和风格过滤掉"随机"选项
    const gs = (genres.value || []).filter(g => g !== '随机');
    form.genre = pick(gs.length ? gs : genres.value);
    const ss = (styles.value || []).filter(s => s !== '随机');
    form.style = pick(ss.length ? ss : styles.value);

    // 金手指相关
    form.golden_finger = Math.random() < 0.35 ? 1 : 0;
    form.golden_finger_type = form.golden_finger ? pick(fingerTypes.value) : '';

    // 全量随机和部分随机模式
    if (randomFull.value) {
        // 全量随机 - 所有字段都赋值
        form.title_pattern = pick(titlePatterns.value);
        form.female_role = pick(femaleRoles.value);
        form.male_role = pick(maleRoles.value);
        form.villain_role = pick(villainRoles.value);
        form.male_job = pick(maleJobs.value);
        form.female_job = pick(femaleJobs.value);
        form.plot = pick(plots.value);
        form.world = pick(worlds.value);
    } else {
        // 部分随机 - 只随机部分主要字段
        form.title_pattern = pick(titlePatterns.value);
        form.female_role = maybe(femaleRoles.value, 'female_role');
        form.male_role = maybe(maleRoles.value, 'male_role');
        // 以下字段设为空，保留用户可能已输入的值
        form.villain_role = form.villain_role || '';
        form.male_job = form.male_job || '';
        form.female_job = form.female_job || '';
        form.plot = form.plot || '';
        form.world = form.world || '';
    }

    // 背景补充固定为"无"，但可以保留用户已修改的值
    if (!form.background || form.background === '无') {
        form.background = '无';
    }
}
function onRandomToggle(v) { if (v === 1) randomize() }

// 上传封面
async function beforeCoverUpload(file) { return await beforeUpload(file, { maxSize: 3 }) }
async function handleCoverUpload(options) {
    try {
        uploadingCover.value = true
        const sig = await getUploadSignature('image')
        const ret = await uploadToOSS(options.file, sig)
        form.cover_url = ret.url
        options.onSuccess({ data: { url: ret.url } })
    } catch (e) {
        options.onError(e)
    } finally {
        uploadingCover.value = false
    }
}

// 创建小说
async function createNovel() {
    try {
        savingNovel.value = true
        const payload = {
            store_id: storeId,
            title: form.title || '未命名长篇',
            cover_url: form.cover_url,
            random_mode: form.random_mode,
            type: form.type, language: form.language, platform: form.platform,
            length_type: form.length_type, pov: form.pov, style_mode: form.style_mode,
            era: form.era, genre: form.genre, golden_finger: form.golden_finger,
            golden_finger_type: form.golden_finger_type, title_pattern: form.title_pattern,
            style: form.style, female_role: form.female_role, male_role: form.male_role,
            villain_role: form.villain_role, male_job: form.male_job, female_job: form.female_job,
            plot: form.plot, world: form.world, background: form.background, chapter_plan: form.chapter_plan,
        }
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/novel_api.php`, payload)
        if (data.code === 200) {
            novelId.value = data.data.id
            chaptersInited.value = false
            inspirationDone.value = false
            outlineDone.value = false
            ElMessage.success('小说创建成功')
        } else {
            ElMessage.error(data.message || '创建失败')
        }
    } finally {
        savingNovel.value = false
    }
}

// 初始化章节
async function initChapters() {
    if (!novelId.value) { ElMessage.warning('请先创建小说'); return }
    const { data } = await axios.post(`${config.BASE_URL}/shop/api/novel_chapter_api.php`, {
        mode: 'bulk_init', store_id: storeId, novel_id: novelId.value
    })
    if (data.code === 200) {
        chaptersInited.value = true
        ElMessage.success(data.message || '初始化完成')
    } else {
        ElMessage.error(data.message || '初始化失败')
    }
}

// 关闭 SSE
function closeES() { if (es) { es.close(); es = null } }

// 按钮可用性
const canGenInspiration = computed(() => !!novelId.value && chaptersInited.value && !es)
const canGenOutline = computed(() => !!novelId.value && chaptersInited.value && inspirationDone.value && !es)
const canGenChapter = computed(() => !!novelId.value && chaptersInited.value && inspirationDone.value && outlineDone.value && chapterIndex.value >= 1 && !es)

// SSE meta
const currentChapterId = ref(null)
const currentSessionId = ref(null)

// 保存：灵感
async function saveInspirationToServer() {
    if (!rawText.value || !novelId.value) return
    const { data } = await axios.post(`${config.BASE_URL}/shop/api/save_inspiration.php`, {
        store_id: storeId, novel_id: novelId.value, inspiration_content: rawText.value
    })
    if (data.code === 200) { inspirationDone.value = true; ElMessage.success('灵感保存成功') }
    else { ElMessage.error(data.message || '保存失败') }
}

// 保存：大纲
async function saveOutlineToServer(content) {
    if (!content || !novelId.value) return
    const { data } = await axios.post(`${config.BASE_URL}/shop/api/save_outline.php`, {
        store_id: storeId, novel_id: novelId.value, outline_content: content
    })
    if (data.code === 200) { outlineDone.value = true; ElMessage.success('大纲保存成功') }
    else { ElMessage.error(data.message || '保存失败') }
}

// 保存：章节（统一调用 save_chapter.php）
async function saveChapterToServer({ content, chapterId, writeMode, action, cursor, addedWords }) {
    if (!content || !novelId.value || !chapterId) return
    const payload = {
        store_id: storeId,
        novel_id: novelId.value,
        chapter_id: chapterId,
        chapter_content: content,
        write_mode: writeMode,         // 'overwrite' | 'append'
        action: action || 'generate',  // 'generate' | 'continue'
        cursor: cursor || null,        // 续写用
        added_words: addedWords || 0   // 续写可传
    }
    const { data } = await axios.post(`${config.BASE_URL}/shop/api/save_chapter.php`, payload)
    if (data.code === 200) ElMessage.success(writeMode === 'append' ? '续写保存成功' : '章节保存成功')
    else ElMessage.error(data.message || '保存失败')
}

// 发起流（仅负责展示 & 收集全文；落库统一在 done 时调用保存接口）
function stream(kind) {
    if (!novelId.value) { ElMessage.warning('请先创建小说'); return }

    // 流程门槛
    if (!chaptersInited.value) { ElMessage.warning('请先【初始化章节架构】'); return }
    if (kind === 'outline' && !inspirationDone.value) { ElMessage.warning('请先生成并保存【灵感】'); return }
    if ((kind === 'chapter' || kind === 'continue') && (!inspirationDone.value || !outlineDone.value)) {
        ElMessage.warning('请先完成【灵感】与【大纲】再生成章节'); return
    }

    // 清理 UI
    closeES(); liveText.value = ''; rawText.value = ''; logs.value = []; queue.value = []; currentChapterId.value = null; currentSessionId.value = null;

    const qs = new URLSearchParams()
    qs.set('store_id', storeId)
    qs.set('novel_id', novelId.value)
    if (kind === 'chapter' || kind === 'continue') qs.set('chapter_idx', String(chapterIndex.value))
    qs.set('mode', kind)
    qs.set('estimate_words', '2000')

    const url = `${config.BASE_URL}/shop/api/ai_stream.php?${qs.toString()}`
    es = new EventSource(url)

    es.onmessage = (e) => {
        if (!e.data) return
        try {
            const obj = JSON.parse(e.data)
            if (obj.event) {
                if (obj.event === 'start') {
                    log('info', obj.message || '开始生成')
                    if (obj.meta && obj.meta.chapter_id) currentChapterId.value = obj.meta.chapter_id
                    if (obj.meta && obj.meta.session_id) currentSessionId.value = obj.meta.session_id
                } else if (obj.event === 'warn') {
                    log('warning', obj.message || '警告')
                } else if (obj.event === 'error') {
                    log('danger', obj.message || '错误'); closeES()
                } else if (obj.event === 'delta') {
                    if (obj.text) pushToType(obj.text)
                } else if (obj.event === 'done') {
                    if (!es) return
                    log('success', '生成完成'); closeES()

                    // —— 统一在这里落库 —— //
                    if (kind === 'inspiration') {
                        saveInspirationToServer()
                    } else if (kind === 'outline') {
                        saveOutlineToServer(rawText.value)
                    } else if (kind === 'chapter') {
                        const cid = currentChapterId.value
                        if (cid) {
                            saveChapterToServer({
                                content: rawText.value,
                                chapterId: cid,
                                writeMode: 'overwrite',
                                action: 'generate'
                            })
                        } else ElMessage.error('未获取到章节ID，保存失败')
                    } else if (kind === 'continue') {
                        const cid = currentChapterId.value
                        if (cid) {
                            // 续写：追加 + 写游标
                            const added = rawText.value.length
                            saveChapterToServer({
                                content: rawText.value,
                                chapterId: cid,
                                writeMode: 'append',
                                action: 'continue',
                                cursor: currentSessionId.value,
                                addedWords: added
                            })
                        } else ElMessage.error('未获取到章节ID，保存失败')
                    }
                }
                return
            }
            // 兜底：标准 chunk
            if (obj.object === 'chat.completion.chunk' && obj.choices?.length) {
                const delta = obj.choices[0].delta || {}
                if (typeof delta.content === 'string') pushToType(delta.content)
                return
            }
            // 文本兜底
            if (typeof obj === 'string') pushToType(obj)
        } catch {
            pushToType(e.data)
        }
    }
    es.onerror = () => { log('danger', '连接中断'); closeES() }
}

onMounted(async () => {
    await loadMeta()
    if (form.random_mode === 1) randomize()

    const qNovelId = Number(route.query.novel_id || 0)
    const qIdx = Number(route.query.chapter_idx || 0)
    const qAuto = String(route.query.autostart || '')

    if (qNovelId) novelId.value = qNovelId
    if (qIdx > 0) chapterIndex.value = qIdx
    if (qAuto && novelId.value) stream(qAuto)
})
</script>

<style scoped lang="scss">
.page-container {
    background: linear-gradient(135deg, #f5f7fa, #e4e8ed);
    min-height: 100vh;
}

.page-header {
    padding: 24px 30px;
    background: #fff;
    margin-bottom: 16px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, .05);
}

.page-title {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
}

.page-subtitle {
    margin-top: 6px;
    color: #666;
}

.studio-layout {
    display: flex;
    gap: 16px;
    margin: 0 auto;
    padding: 0 16px;
}

.left-pane {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.right-pane {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.card {
    border-radius: 12px;
}

.card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid #f0f0f0;
    margin-bottom: 12px;
}

.icon-wrapper {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(64, 158, 255, .1);
    color: #409EFF;
}

.auth-title {
    margin: 0;
    font-weight: 600;
}

.tip-text {
    font-size: 12px;
    color: #888;
}

.elegant-form .el-form-item {
    margin-bottom: 14px;
}

.btn-row {
    display: flex;
    gap: 12px;
}

.stream-box {
    height: 480px;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    padding: 12px;
    background: #fff;
    overflow-y: auto;
}

.stream-text {
    white-space: pre-wrap;
    word-break: break-word;
    font-family: system-ui, -apple-system, "PingFang SC", "Microsoft Yahei", monospace;
    font-size: 14px;
    line-height: 1.75;
}

.avatar-uploader .empty-cover {
    width: 120px;
    height: 160px;
    border: 2px dashed var(--el-border-color);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.cover {
    width: 120px;
    height: 160px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid var(--el-border-color);
}

.plus-icon {
    font-size: 28px;
    color: #999;
}

.loading-icon {
    animation: rotating 2s linear infinite;
    font-size: 26px;
}

@keyframes rotating {
    from {
        transform: rotate(0)
    }

    to {
        transform: rotate(360deg)
    }
}

.mb-12 {
    margin-bottom: 12px;
}

.mr-8 {
    margin-right: 8px;
}

.btn-col {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.rand-row {
    display: flex;
    align-items: center;
    gap: 10px;
}

.tags-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.log-row {
    margin-top: 10px;
}

.uniform-width {
    width: 100%;
}

.el-button+.el-button {
    margin-left: 0 !important;
}

.button-jg {
    margin-left: 12px
}

.button-container {
    display: flex;
    gap: 12px;
}

.mt-8 {
    margin-top: 8px;
}
</style>